import { apiFiles } from "@/api/file.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useFileDropzone } from "@/hooks/use-file-dropzone.hook.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ImageFileTypes } from "@/types/blog.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import {
  Avatar,
  Button,
  Select,
  SelectItem,
  FileDropzone,
  Modal,
  ModalProps,
  Stack,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const cropOutputExtensions: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

const getCropOutputType = (file: File) => {
  return cropOutputExtensions[file.type] ? file.type : "image/jpeg";
};

const getCropOutputFileName = (fileName: string, type: string) => {
  const name = fileName.replace(/\.[^/.]+$/, "");

  return `${name}.${cropOutputExtensions[type] ?? "jpg"}`;
};

export interface ImageUploadModalProps extends ModalProps {
  path: ApiFilePathTypes;
  onSave: (fileId: ApiFile, selectedValue?: string) => Promise<boolean>;
  modalTitle: string;
  generatePublicLink?: boolean;
  entityId?: number;
  withCrop?: boolean;
  minImageSize?: number;
  saveSelect?: {
    label: string;
    options: SelectItem[];
    defaultValue: string;
  };
}

export const ImageUploadModal = ({
  path,
  onSave,
  modalTitle,
  generatePublicLink = false,
  entityId,
  withCrop = false,
  minImageSize,
  saveSelect,
  ...props
}: ImageUploadModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState(saveSelect?.defaultValue);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [newFileUrl, setNewFileUrl] = useState<string | null>(null);
  const [file, setFile] = useState<ApiFile | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [minCropWidth, setMinCropWidth] = useState<number>();
  const [minCropHeight, setMinCropHeight] = useState<number>();
  const imgRef = useRef<HTMLImageElement>(null);
  const { showError } = useToasts();
  const supportedFormats = [".jpg", ".png", ".gif"].join(", ");

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const setObjectPreviewUrl = (file: File) => {
    setPreviewUrl(URL.createObjectURL(file));
  };

  const resetPreview = () => {
    setPreviewUrl(null);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height, naturalWidth, naturalHeight } = e.currentTarget;

    if (minImageSize && (naturalWidth < minImageSize || naturalHeight < minImageSize)) {
      showError({ title: t("errors:image_min_size", { size: minImageSize }) });
      setPendingFile(null);
      resetPreview();
      return;
    }

    const scaleX = naturalWidth / width;
    const scaleY = naturalHeight / height;
    const cropSize = Math.min(width, height);
    const crop = centerCrop(
      makeAspectCrop({ unit: "px", width: cropSize }, 1, width, height),
      width,
      height
    );

    setMinCropWidth(minImageSize ? minImageSize / scaleX : undefined);
    setMinCropHeight(minImageSize ? minImageSize / scaleY : undefined);
    setCrop(crop);
    setCompletedCrop(crop as PixelCrop);
  };

  const getCroppedBlob = (
    image: HTMLImageElement,
    crop: PixelCrop,
    outputType: string
  ): Promise<Blob | null> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = Math.round(crop.width * scaleX);
    canvas.height = Math.round(crop.height * scaleY);

    const ctx = canvas.getContext("2d");
    if (!ctx) return Promise.resolve(null);

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), outputType, 0.95);
    });
  };

  const uploadFile = useCallback(
    async (fileToUpload: File | Blob, fileName?: string) => {
      const formData = new FormData();
      if (entityId) formData.append("entity_id", entityId.toString());
      formData.append("file", fileToUpload, fileName || (fileToUpload as File).name);
      formData.append("path", path);
      formData.append("is_public", "false");
      formData.append("save_original_name", "true");
      formData.append("generate_public_link", generatePublicLink ? "true" : "false");

      setIsUploading(true);

      const { data, error } = await apiFiles.add(formData);

      if (data) {
        setFile(data);
        const { data: signedLink, error } = await apiFiles.getLink(data.uuid, 1);
        if (signedLink) {
          setNewFileUrl(signedLink.download_url);
        } else {
          handleErrors(error);
        }
      } else {
        handleErrors(error);
      }
      setIsUploading(false);

      return data ?? null;
    },
    [entityId, path, generatePublicLink, handleErrors]
  );

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const droppedFile = acceptedFiles[0];

      if (withCrop) {
        setFile(null);
        setNewFileUrl(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        setPendingFile(droppedFile);
        setObjectPreviewUrl(droppedFile);
      } else {
        await uploadFile(droppedFile);
      }
    }
  };

  const handleCropConfirm = async () => {
    if (!pendingFile || !completedCrop || !imgRef.current) return;

    setIsLoading(true);
    try {
      const outputType = getCropOutputType(pendingFile);
      const croppedBlob = await getCroppedBlob(imgRef.current, completedCrop, outputType);
      if (!croppedBlob) return;

      const uploadedFile = await uploadFile(
        croppedBlob,
        getCropOutputFileName(pendingFile.name, outputType)
      );

      if (uploadedFile) {
        const success = await onSave(uploadedFile, selectedValue);
        if (success) {
          setPendingFile(null);
          resetPreview();
          close(id);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const { onDropRejected } = useFileDropzone({
    tooManyFilesMessage: t("errors:only_one_file_allowed", {
      formats: supportedFormats,
    }),
    unsupportedFileFormatMessage: t("errors:unsupported_file_format", {
      formats: supportedFormats,
    }),
  });

  const handleSave = useCallback(async () => {
    if (!file) {
      showError({ title: t("errors:no_file_selected") });
      return;
    }
    setIsLoading(true);
    const success = await onSave(file, selectedValue);
    if (success) {
      close(id);
    }
    setIsLoading(false);
  }, [file, selectedValue, onSave, showError, t, close, id]);

  return (
    <Modal
      {...props}
      variant={"side"}
      title={modalTitle}
      layout={"stretch"}
      withCloser={true}
      actions={
        withCrop && !previewUrl ? undefined : (
          <Stack gap={"sm"}>
            {withCrop ? (
              <>
                {previewUrl && (
                  <Button
                    size={"lg"}
                    className={"w-full"}
                    onClick={handleCropConfirm}
                    isLoading={isUploading || isLoading}
                  >
                    {t("common:save")}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button size={"lg"} className={"w-full"} isLoading={isLoading} onClick={handleSave}>
                  {t("common:save")}
                </Button>
                <Button
                  variant={"plain"}
                  size={"lg"}
                  onClick={() => {
                    close(id);
                  }}
                >
                  {t("common:cancel")}
                </Button>
              </>
            )}
          </Stack>
        )
      }
    >
      <>
        {withCrop && previewUrl ? (
          <Stack gap={"md"} className={"p-4"}>
            <ReactCrop
              crop={crop}
              onChange={(c: Crop) => setCrop(c)}
              onComplete={(c: PixelCrop) => setCompletedCrop(c)}
              aspect={1}
              minWidth={minCropWidth}
              minHeight={minCropHeight}
            >
              <img
                ref={imgRef}
                src={previewUrl}
                onLoad={onImageLoad}
                alt="Crop preview"
                style={{ maxWidth: "100%" }}
              />
            </ReactCrop>
          </Stack>
        ) : (
          <FileDropzone
            onDrop={onDrop}
            accept={Object.fromEntries(
              Object.entries(ImageFileTypes).map(([key, value]) => [
                value,
                [`.${key.toLowerCase()}`],
              ])
            )}
            onDropRejected={onDropRejected}
            descriptionSize="sm"
            description={t("common:accepts_formats", {
              formats: supportedFormats,
            })}
            ctaActionText={t("common:image").toLowerCase()}
            activeDescription={t("common:upload_file_drag_and_drop")}
            isLoading={isUploading}
            required
            multiple={false}
            maxFiles={1}
          >
            {newFileUrl ? (
              <Stack items={"center"} justify={"center"} className={"min-h-56 p-8"}>
                <Avatar url={newFileUrl} size={"2xl"} />
              </Stack>
            ) : undefined}
          </FileDropzone>
        )}
        {saveSelect && (
          <div className={"mt-4"}>
            <Select
              label={saveSelect.label}
              emptyTitle={t("common:select")}
              options={saveSelect.options}
              value={selectedValue}
              onChange={(value) => {
                if (typeof value === "string") setSelectedValue(value);
              }}
              isClearable={false}
              disabled={isLoading || isUploading}
            />
          </div>
        )}
      </>
    </Modal>
  );
};
